import { berlinZeitpunkt } from "@dj/domain/gemeinsam";
import { useDokument, useSammlung, type Zeile } from "../plattform/lesemodelle";
import { idToken, useSitzung } from "../plattform/sitzung";
import { apiFetch } from "../plattform/version";

/**
 * Gemeinsames der Ansichten des Kontexts Strecke: Rahmen einer Ansicht der Drückjagd (Zustandsband, `main`; geliefert aus
 * `App.tsx`, weil die Ansicht der Drückjagd dem Kontext Drückjagd gehört), die Lesemodelle (lesemodelle.md §3, §4, §6) und
 * die Darstellung der Werte in Tabellen (oberflaeche.md §9: Zeitpunkte als `JJJJ-MM-TT hh:mm`, kein Wert als `—`).
 */
export type { Rahmen, RahmenProps } from "../plattform/rahmen";

export const KEIN_WERT = "—";

export function wert(w: unknown): string {
  if (w === null || w === undefined || w === "") return KEIN_WERT;
  if (w instanceof Date) return berlinZeitpunkt(w);
  if (typeof w === "boolean") return w ? "ja" : "nein";
  if (typeof w === "object" && w && "Name" in w) return String((w as { Name: unknown }).Name) || KEIN_WERT;
  if (typeof w === "object" && w && "Breite" in w && "Länge" in w) {
    const p = w as { Breite: number; Länge: number };
    return `${Number(p.Breite).toFixed(6)}, ${Number(p.Länge).toFixed(6)}`;
  }
  return String(w);
}

export function nachReihenfolge(zeilen: readonly Zeile[]): Zeile[] {
  return [...zeilen].sort((a, b) => Number(a["_reihenfolge"] ?? 0) - Number(b["_reihenfolge"] ?? 0));
}

/** Eigener Eintrag in Meine Drückjagden: Revier (`_kennungen.Revier`) und Rollen der Person in dieser Drückjagd. */
export function useEigeneLage(drueckjagdId: string): { revier: string | null; rollen: string[] } {
  const { uid } = useSitzung();
  const eintrag = useDokument(uid ? `personen/${uid}/meine_drueckjagden/${drueckjagdId}` : null);
  const revier = (eintrag.daten?.["_kennungen"] as Record<string, string> | undefined)?.["Revier"] ?? null;
  return { revier, rollen: (eintrag.daten?.["Rollen"] as string[] | undefined) ?? [] };
}

export function istLeitung(rollen: readonly string[]): boolean {
  return rollen.includes("Jagdherr") || rollen.includes("Jagdleiter");
}

export function useStrecke(drueckjagdId: string) {
  const eintraege = useSammlung(`drueckjagden/${drueckjagdId}/strecke`);
  const stand = useDokument(`drueckjagden/${drueckjagdId}/strecke_stand/aktuell`);
  const zusammengefasst = useSammlung(`drueckjagden/${drueckjagdId}/strecke_zusammengefasst`);
  return { eintraege: nachReihenfolge(eintraege.daten), stand: stand.daten, zusammengefasst: nachReihenfolge(zusammengefasst.daten), laedt: eintraege.laedt };
}

/** Lesemodell eines Reviers in der Drückjagd (lesemodelle.md §4), Tabellensammlung in ihrer Reihenfolge. */
export function useRevierteil(drueckjagdId: string, revier: string | null, teil: string): Zeile[] {
  return nachReihenfolge(useSammlung(revier ? `drueckjagden/${drueckjagdId}/reviere/${revier}/${teil}` : null).daten);
}

const WARTEN_MS = 1000;
const HOECHSTENS_VERSUCHE = 30;

/**
 * Lädt eine exportierte Datei über `GET /api/dateien/<DateiId>` (medien.md §7), sobald der Worker sie abgelegt hat
 * (`datei-nicht-bereit`), und übergibt sie dem Gerät zum Speichern; liefert bei Abweisung den Grund.
 */
export async function dateiHerunterladen(url: string): Promise<string | null> {
  for (let versuch = 0; versuch < HOECHSTENS_VERSUCHE; versuch++) {
    const token = await idToken();
    const r = await apiFetch(url, { headers: token ? { Authorization: `Bearer ${token}` } : {} }).catch(() => null);
    if (r?.status === 200) {
      const name = /filename\*=UTF-8''([^;]+)/.exec(r.headers.get("Content-Disposition") ?? "")?.[1];
      const adresse = URL.createObjectURL(await r.blob());
      const a = document.createElement("a");
      a.href = adresse;
      a.download = name ? decodeURIComponent(name) : "export";
      a.click();
      setTimeout(() => URL.revokeObjectURL(adresse), WARTEN_MS * 10);
      return null;
    }
    if (r && r.status !== 409) return ((await r.json().catch(() => ({}))) as { grund?: string }).grund ?? "kein-zugriff";
    await new Promise((weiter) => setTimeout(weiter, WARTEN_MS));
  }
  return "datei-nicht-bereit";
}

/** Symbol einer Ansprache (design/README §8.5): Altersklasse bei Rot- und Damwild, sonst die Wildart. */
export function symbolDerAnsprache(ansprache: string): string {
  const [wildart = "", altersklasse] = ansprache.split(" ");
  if (altersklasse === "Hirsch") return wildart === "Damwild" ? "hirsch-dam" : "hirsch-rot";
  if (altersklasse) return altersklasse.toLowerCase();
  return ({ Raubwild: "raubwild", Rehwild: "rehwild", Damwild: "damwild", Rotwild: "rotwild", Schwarzwild: "schwarzwild" } as Record<string, string>)[wildart] ?? "andere-wildart";
}
